import dayjs from 'dayjs';
import {
  OperatingHour,
  OperationPolicy,
  SlotsMap,
  CalendarSummaryMap,
  DayStatus,
  SlotsMapWithData,
} from '@/types/schedule';

// ─── 시간 ──────────────────────────────────────────────────────────────────────

export function minuteOfDayToTime(min: number): string {
  const h = Math.floor(min / 60).toString().padStart(2, '0');
  const m = (min % 60).toString().padStart(2, '0');
  return `${h}:${m}`;
}

export function dateToString(date: Date): string {
  const y = date.getFullYear();
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const d = date.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}
export const formatDateWithDay = (dateStr: string) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
  return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일 (${dayNames[date.getDay()]})`;
};
export function getWeekStart(date: Date): Date {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getWeekDates(weekStart: Date): Date[] {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
}
export function formatTimeCode(timeCode: number): string {
  const hour = Math.floor(timeCode / 100);
  const minuteCode = timeCode % 100;
  const minute = Math.round((minuteCode / 100) * 60);
  return `${`${hour}`.padStart(2, "0")}:${`${minute}`.padStart(2, "0")}`;
}

export function formatDateFull(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  const days = ["일", "월", "화", "수", "목", "금", "토"];
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${days[d.getDay()]})`;
}

// ─── 블록 기반 운영시간 ────────────────────────────────────────────────────────

export type OperatingHourBlock = {
  startMinuteOfDay: number;
  endMinuteOfDay: number;
};

export type OperatingHourWeekdayRule = {
  weekday: number;
  useCustomYn: number;
  isClosedYn: number;
  blocks: OperatingHourBlock[];
};

export type OperatingHourSettings = {
  defaultBlocks: OperatingHourBlock[];
  weekdays: OperatingHourWeekdayRule[];
};

type PolicyLike = OperationPolicy & {
  operatingHourSettings?: OperatingHourSettings | null;
};

function normalizeBlocks(blocks: any[] = []): OperatingHourBlock[] {
  return [...blocks]
    .map((block) => ({
      startMinuteOfDay: Number(block.startMinuteOfDay),
      endMinuteOfDay: Number(block.endMinuteOfDay),
    }))
    .filter(
      (block) =>
        Number.isInteger(block.startMinuteOfDay) &&
        Number.isInteger(block.endMinuteOfDay) &&
        block.startMinuteOfDay < block.endMinuteOfDay
    )
    .sort((a, b) => a.startMinuteOfDay - b.startMinuteOfDay);
}

function legacyOperatingHourToBlocks(
  operatingHour?: {
    openMinuteOfDay: number | null;
    closeMinuteOfDay: number | null;
    lunchStartMinuteOfDay: number | null;
    lunchEndMinuteOfDay: number | null;
    activeYn: number;
  } | null
): OperatingHourBlock[] {
  if (!operatingHour || Number(operatingHour.activeYn) !== 1) return [];

  const openMinuteOfDay =
    operatingHour.openMinuteOfDay == null ? null : Number(operatingHour.openMinuteOfDay);
  const closeMinuteOfDay =
    operatingHour.closeMinuteOfDay == null ? null : Number(operatingHour.closeMinuteOfDay);
  const lunchStartMinuteOfDay =
    operatingHour.lunchStartMinuteOfDay == null
      ? null
      : Number(operatingHour.lunchStartMinuteOfDay);
  const lunchEndMinuteOfDay =
    operatingHour.lunchEndMinuteOfDay == null
      ? null
      : Number(operatingHour.lunchEndMinuteOfDay);

  if (openMinuteOfDay == null || closeMinuteOfDay == null) return [];

  if (lunchStartMinuteOfDay != null && lunchEndMinuteOfDay != null) {
    return normalizeBlocks([
      {
        startMinuteOfDay: openMinuteOfDay,
        endMinuteOfDay: lunchStartMinuteOfDay,
      },
      {
        startMinuteOfDay: lunchEndMinuteOfDay,
        endMinuteOfDay: closeMinuteOfDay,
      },
    ]);
  }

  return normalizeBlocks([
    {
      startMinuteOfDay: openMinuteOfDay,
      endMinuteOfDay: closeMinuteOfDay,
    },
  ]);
}

export function getOperatingHour(
  operatingHours: OperatingHour[],
  weekday: number
): OperatingHour | undefined {
  return operatingHours.find((h) => Number(h.weekday) === Number(weekday));
}

export function getOperatingBlocks(
  policyOrSettings: PolicyLike | OperatingHourSettings | null | undefined,
  weekday: number
): OperatingHourBlock[] {
  if (!policyOrSettings) return [];

  const settings =
    'operatingHourSettings' in (policyOrSettings as any)
      ? (policyOrSettings as PolicyLike).operatingHourSettings
      : (policyOrSettings as OperatingHourSettings);

  if (settings) {
    const defaultBlocks = normalizeBlocks(settings.defaultBlocks || []);
    const rule = (settings.weekdays || []).find(
      (item) => Number(item.weekday) === Number(weekday)
    );

    if (!rule) return defaultBlocks;
    if (Number(rule.isClosedYn) === 1) return [];
    if (Number(rule.useCustomYn) === 1) return normalizeBlocks(rule.blocks || []);
    return defaultBlocks;
  }

  const policy = policyOrSettings as PolicyLike;
  return legacyOperatingHourToBlocks(getOperatingHour(policy.operatingHours || [], weekday));
}

// ─── SlotsMap ─────────────────────────────────────────────────────────────────

export function normalizeMinuteArray(arr: number[]): number[] {
  return [...new Set(arr)].sort((a, b) => a - b);
}

export function cloneSlotsMap(slots: SlotsMap): SlotsMap {
  const result: SlotsMap = {};
  for (const [date, roomMap] of Object.entries(slots)) {
    result[date] = {};
    for (const [roomId, minutes] of Object.entries(roomMap)) {
      result[date][roomId] = [...minutes];
    }
  }
  return result;
}

export function buildSlotsMapFromOpenSlots(openSlots = []) {
  const openSlotStatusMap = {}, bookedStatusMap ={}
  openSlots.forEach((slot) => {
    if (
      !slot ||
      !slot.slotDate ||
      slot.roomId == null ||
      slot.slotStatus == 'CLOSED' ||
      slot.startMinuteOfDay == null
    ) {
      return;
    }
    const slotDate = dayjs(slot.slotDate).format('YYYY-MM-DD');
    const roomId = String(slot.roomId);
    const startMinuteOfDay = Number(slot.startMinuteOfDay);
    if (slot.slotStatus == 'BOOKED') {
       if (!bookedStatusMap[slotDate]) {
        bookedStatusMap[slotDate] = {};
      }

      if (!bookedStatusMap[slotDate][roomId]) {
        bookedStatusMap[slotDate][roomId] = {};
      }
      bookedStatusMap[slotDate][roomId][startMinuteOfDay] = slot;
      return;
    }

    if (!openSlotStatusMap[slotDate]) {
      openSlotStatusMap[slotDate] = {};
    }
    if (!openSlotStatusMap[slotDate][roomId]) {
      openSlotStatusMap[slotDate][roomId] = [];
    }
    openSlotStatusMap[slotDate][roomId].push(startMinuteOfDay);
  });

  Object.keys(openSlotStatusMap).forEach((slotDate) => {
    Object.keys(openSlotStatusMap[slotDate]).forEach((roomId) => {
      openSlotStatusMap[slotDate][roomId] = normalizeMinuteArray(openSlotStatusMap[slotDate][roomId]);
    });
  });
  // Object.keys(openSlotStatusMap).forEach((slotDate) => {
  //   Object.keys(bookedStatusMap[slotDate]).forEach((roomId) => {
  //     bookedStatusMap[slotDate][roomId] = normalizeMinuteArray(bookedStatusMap[slotDate][roomId]);
  //   });
  // });

  return { openSlotStatusMap, bookedStatusMap };
}

// ─── Slot 상태 계산 ────────────────────────────────────────────────────────────

export function isDurationBlockedByBlocks({
  blocks,
  startMinuteOfDay,
  durationMinutes,
}: {
  blocks?: OperatingHourBlock[] | null;
  startMinuteOfDay: number;
  durationMinutes?: number;
}) {
  const normalized = normalizeBlocks(blocks || []);
  if (!normalized.length) return true;

  const endMinute = startMinuteOfDay + (durationMinutes ?? 0);

  return !normalized.some((block) => {
    if (durationMinutes != null) {
      return (
        startMinuteOfDay >= block.startMinuteOfDay &&
        endMinute <= block.endMinuteOfDay
      );
    }

    return (
      startMinuteOfDay >= block.startMinuteOfDay &&
      startMinuteOfDay < block.endMinuteOfDay
    );
  });
}

export function isDurationBlocked({
  startMinuteOfDay,
  operatingHour,
  durationMinutes,
}: {
  startMinuteOfDay: number;
  operatingHour?: {
    openMinuteOfDay: number | null;
    closeMinuteOfDay: number | null;
    lunchStartMinuteOfDay: number | null;
    lunchEndMinuteOfDay: number | null;
    activeYn: number;
  } | null;
  durationMinutes?: number;
}) {
  const blocks = legacyOperatingHourToBlocks(operatingHour);
  return isDurationBlockedByBlocks({
    blocks,
    startMinuteOfDay,
    durationMinutes,
  });
}

export function getVisibleTimeAxis(
  dates: Date[],
  operatingHoursOrPolicy: OperatingHour[] | PolicyLike,
  sessionMinutes: number
): number[] {
  let minMinute = Infinity;
  let maxMinute = -Infinity;

  dates.forEach((date) => {
    const weekday = date.getDay();

    const blocks = Array.isArray(operatingHoursOrPolicy)
      ? legacyOperatingHourToBlocks(
          getOperatingHour(operatingHoursOrPolicy, weekday)
        )
      : getOperatingBlocks(operatingHoursOrPolicy, weekday);

    blocks.forEach((block) => {
      if (block.startMinuteOfDay < minMinute) {
        minMinute = block.startMinuteOfDay;
      }
      if (block.endMinuteOfDay > maxMinute) {
        maxMinute = block.endMinuteOfDay;
      }
    });
  });

  if (minMinute === Infinity || maxMinute === -Infinity) {
    return [];
  }

  const times = [];
  for (
    let minute = minMinute;
    minute + sessionMinutes <= maxMinute;
    minute += sessionMinutes
  ) {
    times.push(minute);
  }

  return times;
}

export function formatBlocksLabel(blocks: OperatingHourBlock[]): string {
  const normalized = normalizeBlocks(blocks);
  if (!normalized.length) return '휴무';
  return normalized
    .map((block) => `${minuteOfDayToTime(block.startMinuteOfDay)}~${minuteOfDayToTime(block.endMinuteOfDay)}`)
    .join(' / ');
}

// ─── 날짜 상태 계산 ────────────────────────────────────────────────────────────

export function getMonthDates(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());

  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

export function isSameDate(a: Date, b: Date): boolean {
  return dateToString(a) === dateToString(b);
}

export function isToday(date: Date): boolean {
  return isSameDate(date, new Date());
}

export function isBeforeNow(date: Date): boolean {
  const target = new Date(date);
  target.setHours(23, 59, 59, 999);
  return target.getTime() < Date.now();
}

export function getDateStatus(
  date: Date,
  summary?: CalendarSummaryMap
): DayStatus {
  const key = dateToString(date);
  return summary?.[key]?.status ?? 'closed';
}

export function buildCalendarSummaryMap(
  slotsMap: SlotsMap,
  operationPolicy: PolicyLike,
  monthDates: Date[]
): CalendarSummaryMap {
  const result: CalendarSummaryMap = {};
  const sessionMinutes = operationPolicy.bookingPolicy?.sessionMinutes ?? 60;
  const activeRooms = (operationPolicy.rooms || []).filter(
    (room: any) => Number(room.activeYn ?? 1) === 1
  );

  monthDates.forEach((date) => {
    const key = dateToString(date);
    const blocks = getOperatingBlocks(operationPolicy, date.getDay());

    if (!blocks.length || activeRooms.length === 0) {
      result[key] = {
        total: 0,
        open: 0,
        status: 'closed',
      };
      return;
    }

    let slotsPerRoom = 0;
    blocks.forEach((block) => {
      for (
        let minute = block.startMinuteOfDay;
        minute + sessionMinutes <= block.endMinuteOfDay;
        minute += sessionMinutes
      ) {
        slotsPerRoom += 1;
      }
    });

    const total = slotsPerRoom * activeRooms.length;

    let open = 0;
    activeRooms.forEach((room: any) => {
      open += slotsMap[key]?.[String(room.id)]?.length ?? 0;
    });

    let status: DayStatus = 'closed';
    if (total === 0) {
      status = 'closed';
    } else if (open === 0) {
      status = 'closed';
    } else if (open >= total) {
      status = 'open';
    } else {
      status = 'partial';
    }

    result[key] = {
      total,
      open,
      status,
    };
  });

  return result;
}


export const isOpen = ({openSlotStatusMap, slotDate, roomId, minute}: {openSlotStatusMap: SlotsMap, slotDate: string, roomId: string, minute: number}): boolean =>
  openSlotStatusMap?.[slotDate]?.[roomId]?.includes(minute) ?? false;

export const isBooked = ({bookedStatusMap, slotDate, roomId, minute}: {bookedStatusMap: SlotsMapWithData, slotDate: string, roomId: string, minute: number}): boolean =>
  !!bookedStatusMap?.[slotDate]?.[roomId]?.[minute];

export const getBookingData = ({bookedStatusMap, slotDate, roomId, minute}: {bookedStatusMap: SlotsMapWithData, slotDate: string, roomId: string, minute: number}): any =>
  bookedStatusMap?.[slotDate]?.[roomId]?.[minute];


const isPastSlot = (date, startMinuteOfDay) => {
  const slotDateTime = new Date(date);
  slotDateTime.setHours(0, 0, 0, 0);

  const hour = Math.floor(startMinuteOfDay / 60);
  const minute = startMinuteOfDay % 60;

  slotDateTime.setHours(hour, minute, 0, 0);

  // const slotEndTime = new Date(slotDateTime);
  // slotEndTime.setMinutes(slotEndTime.getMinutes() + sessionMinutes);

  return slotDateTime.getTime() <= Date.now();
}

export const isCellChangable = (date: string | Date, startMinuteOfDay?: number | string) => {
  if (isPastSlot(date, startMinuteOfDay)) {
    return false
  }
  return true
}